import { useState, useEffect, useRef, useCallback, useMemo } from "react";

export default function useInfiniteScroll<TNode>(
  fetchData: any,
  params: { [key in string]: any },
  dependencies: any[] = []
) {
  const { threshold = 0.8, offset = 0, limit: initialPageSize = 20, concatData = false } = params;
  const [data, setData] = useState<TNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const observerRef = useRef<any>();
  const paramsRef = useRef(params);
  const [isInitLoad, setIsInitLoad] = useState(true);
  const [paginationInfo, setPaginationInfo] = useState({
    totalCount: 0,
    offset,
    limit: initialPageSize,
    hasNextPage: true,
    hasPreviousPage: false,
    add: concatData
  });
  const prevDepsRef = useRef<any[] | undefined>(undefined);
  const latestRequestRef = useRef(0);

  const fetchPage = useCallback(
    async ({ offset, limit = 20, add }: { offset: number; limit?: number; add: boolean }) => {
      const request = ++latestRequestRef.current;
      const isLatest = () => request === latestRequestRef.current;
      setLoading(true);
      setError(null);
      try {
        const response = await fetchData({ ...paramsRef.current, offset, limit });
        if (!isLatest()) return;
        const { results, count, next, previous } = response.data;
        setPaginationInfo({
          offset,
          limit: limit ?? paginationInfo.limit,
          totalCount: count,
          hasNextPage: Boolean(next || count > offset + (limit ?? paginationInfo.limit)),
          hasPreviousPage: Boolean(previous),
          add
        });
        setData((prev) => (add ? [...prev, ...results] : results));
      } catch (err: any) {
        if (isLatest()) setError(err);
      } finally {
        if (isLatest()) {
          setLoading(false);
          setIsInitLoad(false);
        }
      }
    },
    [fetchData]
  );

  useEffect(() => {
    if (isInitLoad || !paginationInfo.hasNextPage || !observerRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isInitLoad && paginationInfo.hasNextPage) {
          fetchPage({
            ...paginationInfo,
            offset: paginationInfo.offset + paginationInfo.limit,
            add: true
          });
        }
      },
      { threshold }
    );
    const el = observerRef.current;
    observer.observe(el);

    return () => {
      observer.unobserve(el);
    };
  }, [
    JSON.stringify(params),
    isInitLoad,
    paginationInfo.offset,
    paginationInfo.limit,
    paginationInfo.hasNextPage
  ]);

  const memoizedDependencies = useMemo(() => dependencies, [...dependencies]);

  useEffect(() => {
    const prevDeps = prevDepsRef.current || [];
    const dependenciesChanged = memoizedDependencies.some((dep, idx) => dep !== prevDeps[idx]);

    if (
      dependenciesChanged &&
      memoizedDependencies.every((dep) => !!dep) &&
      paginationInfo.totalCount === 0
    ) {
      fetchPage({ offset: 0, add: false });
    }

    prevDepsRef.current = memoizedDependencies;
  }, [memoizedDependencies, paginationInfo.totalCount, fetchPage]);

  const deleteItem = (id: string) => {
    setData((prev) => prev.filter((item: any) => item.id !== id));
  };

  const loadNextPage = useCallback(() => {
    if (paginationInfo.hasNextPage) {
      fetchPage({
        ...paginationInfo,
        offset: paginationInfo.offset + paginationInfo.limit,
        add: concatData
      });
    }
  }, [paginationInfo, fetchPage]);

  const loadPreviousPage = useCallback(() => {
    if (paginationInfo.hasPreviousPage) {
      fetchPage({
        ...paginationInfo,
        offset: paginationInfo.offset - paginationInfo.limit,
        add: false
      });
    }
  }, [paginationInfo, fetchPage]);

  const goToPage = useCallback(
    (pageNumber: number) => {
      const offset = (pageNumber - 1) * paginationInfo.limit;
      fetchPage({ ...paginationInfo, offset, add: false });
    },
    [paginationInfo.limit, fetchPage]
  );

  const setPageSizeFn = useCallback(
    (newPageSize: number) => {
      fetchPage({ offset: 0, add: false, limit: newPageSize });
    },
    [fetchPage]
  );

  const resetPagination = useCallback(async () => {
    return new Promise<void>((resolve) => {
      setPaginationInfo({
        offset: 0,
        limit: 20,
        totalCount: 0,
        hasNextPage: false,
        hasPreviousPage: false,
        add: false
      });

      fetchPage({ offset: 0, add: false, limit: 20 }).then(() => {
        resolve();
      });
    });
  }, [initialPageSize, fetchPage]);

  return {
    data,
    loading,
    error,
    observerRef,
    paginationInfo,
    deleteItem,
    loadNextPage,
    loadPreviousPage,
    goToPage,
    setPageSizeFn,
    resetPagination,
    fetch: fetchPage
  };
}
